import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Input, Button, Steps, Select, InputNumber, Checkbox, Alert, Space, message } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined, FunnelPlotOutlined } from '@ant-design/icons';
import { getSlotConditions, CONDITION_CN_NUMS } from '../recommendSlotConditions';
import { RECOMMEND_USER_SEGMENTS, RECOMMEND_PACKAGES } from '../recommendMockData';
import RecommendTemplatePanel from './RecommendTemplatePanel';
import { RecommendTemplateManageDialog } from './RecommendTemplateDialogs';
import { DomesticPrice, OverseasPrice, CostPrice } from './PackagePriceCells';

const checkboxGroupStyle = { display: 'flex', flexDirection: 'column', gap: 12, width: '100%' };
const priceFieldLabel = { fontSize: 12, color: '#bfbfbf', marginBottom: 2 };

// 套餐售价 / 底价来源《套餐配置》，只读
const findPackage = (packageId) => RECOMMEND_PACKAGES.find(p => p.packageId === packageId);

/**
 * 推荐位策略 - 添加/编辑抽屉（3步向导）
 * 真实项目：views/operation/recommended/components/AddStrategyDrawer.vue
 */
const RecommendStrategyDrawer = ({ open, slot, editData, funnels, templates, onCancel, onSuccess, onSwitchTab, onTemplatesChange }) => {
    const [form] = Form.useForm();
    const [step, setStep] = useState(0);
    const [rules, setRules] = useState([]);
    const [expandedLayer, setExpandedLayer] = useState(null);
    const [layerCache, setLayerCache] = useState({});
    const [manageOpen, setManageOpen] = useState(false);

    const isEdit = !!editData;
    const isDefaultStrategy = !!editData?.isDefault;
    const slotCode = slot?.slotCode;
    const conditionList = useMemo(() => getSlotConditions(slotCode), [slotCode]);
    const slotFunnels = useMemo(() => funnels.filter(f => f.slotCode === slotCode), [funnels, slotCode]);

    // 打开时初始化
    useEffect(() => {
        if (!open) return;
        setStep(0);
        setExpandedLayer(null);
        const conditions = getSlotConditions(slotCode);
        if (editData) {
            const backendRules = (editData.funnelRules || []).map(r => ({ condition: r.condition, funnelId: r.funnelId, templateIds: r.templateIds || [] }));
            const existing = new Set(backendRules.map(r => r.condition));
            conditions.forEach(c => { if (!existing.has(c.condition)) backendRules.push({ condition: c.condition, funnelId: null, templateIds: [] }); });
            backendRules.sort((a, b) => a.condition - b.condition);
            setRules(backendRules);
            form.setFieldsValue({
                name: editData.name,
                remark: editData.remark,
                popupInterval: editData.popupInterval ?? null,
                cooldownDays: editData.cooldownDays ?? null,
                deliveryRegion: editData.deliveryRegion === 3 ? [1, 2] : (editData.deliveryRegion ? [editData.deliveryRegion] : []),
                userSegments: editData.userSegments || [],
            });
            const cache = {};
            backendRules.forEach(r => {
                if (r.funnelId) {
                    const f = funnels.find(x => x.id === r.funnelId);
                    if (f) cache[r.funnelId] = (f.funnelConfig || []).filter(l => l.packageId);
                }
            });
            setLayerCache(cache);
        } else {
            setRules(conditions.map(c => ({ condition: c.condition, funnelId: null, templateIds: [] })));
            form.resetFields();
            form.setFieldsValue({ popupInterval: 2, cooldownDays: null, deliveryRegion: [], userSegments: [] });
            setLayerCache({});
        }
    }, [open, editData, slotCode, funnels, form]);

    const getRule = (condition) => rules.find(r => r.condition === condition);
    const getLayers = (condition) => layerCache[getRule(condition)?.funnelId] || [];

    const onFunnelSelect = (condition, funnelId) => {
        if (!funnelId) {
            setRules(prev => prev.map(r => (r.condition === condition ? { ...r, funnelId: null, templateIds: [] } : r)));
            return;
        }
        const funnel = funnels.find(f => f.id === funnelId);
        const layers = (funnel?.funnelConfig || []).filter(l => l.packageId);
        setLayerCache(c => ({ ...c, [funnelId]: layers }));
        setRules(prev => prev.map(r => (r.condition === condition ? { ...r, funnelId, templateIds: Array.from({ length: layers.length }, () => '') } : r)));
    };

    const selectTemplate = (condition, layerIndex, code) => {
        setRules(prev => prev.map(r => {
            if (r.condition !== condition) return r;
            const templateIds = [...(r.templateIds || [])];
            while (templateIds.length <= layerIndex) templateIds.push('');
            templateIds[layerIndex] = code;
            return { ...r, templateIds };
        }));
    };

    const templateName = (code) => templates.find(t => t.code === code)?.name || '';

    const handleNext = async () => {
        if (step === 0) { await form.validateFields(['name']); setStep(1); }
        else if (step === 1) { await form.validateFields(['popupInterval']); setStep(2); }
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        if (!values.deliveryRegion?.length) { form.setFields([{ name: 'deliveryRegion', errors: ['请至少选择一个投放区域'] }]); return; }
        if (!isDefaultStrategy && !values.userSegments?.length) { form.setFields([{ name: 'userSegments', errors: ['请至少选择一个投放用户分群'] }]); return; }
        if (values.cooldownDays !== null && values.cooldownDays !== undefined && values.cooldownDays < 1) { message.warning('冷却期最小为1天'); return; }
        onSuccess({
            id: editData?.id,
            appId: slot?.appId,
            slotCode: slot?.slotCode,
            name: values.name,
            remark: values.remark,
            funnelRules: rules.filter(r => r.funnelId).map(r => ({ condition: r.condition, funnelId: r.funnelId, templateIds: r.templateIds || [] })),
            popupInterval: values.popupInterval ? Number(values.popupInterval) : 0,
            cooldownDays: values.cooldownDays || null,
            deliveryRegion: values.deliveryRegion.length === 2 ? 3 : values.deliveryRegion[0],
            userSegments: values.userSegments || [],
        });
        message.success(isEdit ? '编辑成功' : '添加成功');
    };

    return (
        <Drawer
            title={isEdit ? '编辑推荐位策略' : '添加推荐位策略'}
            width={600}
            open={open}
            onClose={onCancel}
            destroyOnClose
            maskClosable={false}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {step > 0 ? <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(step - 1)}>上一步</Button> : <span />}
                    <Space>
                        <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                        {step < 2
                            ? <Button type="primary" icon={<ArrowRightOutlined />} onClick={handleNext}>下一步</Button>
                            : <Button type="primary" icon={<CheckOutlined />} onClick={handleSubmit}>提交</Button>}
                    </Space>
                </div>
            }
        >
            <div style={{ padding: '20px 40px 30px', background: '#f5f7fa', margin: '-20px -20px 20px' }}>
                <Steps current={step} size="small" items={[{ title: '基础信息' }, { title: '漏斗配置' }, { title: '投放配置' }]} />
            </div>

            <Form form={form} layout="vertical">
                {/* 第一步：基础信息 */}
                <div style={{ display: step === 0 ? 'block' : 'none' }}>
                    <Form.Item label="策略名称" name="name" rules={[{ required: true, message: '请输入策略名称' }]}>
                        <Input placeholder="请输入策略名称" maxLength={50} showCount />
                    </Form.Item>
                    <Form.Item label="备注" name="remark">
                        <Input.TextArea placeholder="请输入备注信息" rows={6} maxLength={200} showCount />
                    </Form.Item>
                </div>

                {/* 第二步：漏斗配置 */}
                <div style={{ display: step === 1 ? 'block' : 'none' }}>
                    <p style={{ fontSize: 14, marginBottom: 20 }}>
                        如需变更漏斗，请至
                        <Button type="link" icon={<FunnelPlotOutlined />} style={{ padding: 0, marginLeft: 4 }} onClick={() => { onCancel(); onSwitchTab?.('slot'); }}>【推荐位套餐漏斗】</Button>
                        中配置
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {conditionList.map(item => {
                                const rule = getRule(item.condition) || {};
                                const layers = getLayers(item.condition);
                                return (
                                    <div key={item.condition} style={{ padding: 16, border: '1px solid #dcdfe6', borderLeft: '3px solid #409eff', borderRadius: 4 }}>
                                        <div style={{ fontSize: 14, color: '#606266', marginBottom: 12 }}>
                                            条件{CONDITION_CN_NUMS[item.condition - 1] || item.condition}：{item.label}
                                        </div>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: '#303133', marginBottom: 8 }}>选择漏斗</div>
                                        <Select
                                            style={{ width: '100%' }}
                                            placeholder="请选择漏斗"
                                            allowClear
                                            value={rule.funnelId || undefined}
                                            options={slotFunnels.map(f => ({ label: f.name, value: f.id }))}
                                            onChange={val => onFunnelSelect(item.condition, val)}
                                        />

                                        {rule.funnelId && (
                                            <div style={{ background: '#f5f7fa', borderRadius: 6, padding: 12, marginTop: 12 }}>
                                                {layers.map((pkg, layerIndex) => {
                                                    const key = `${item.condition}-${layerIndex}`;
                                                    const expanded = expandedLayer === key;
                                                    const code = rule.templateIds?.[layerIndex];
                                                    const pkgInfo = findPackage(pkg.packageId);
                                                    return (
                                                        <div key={layerIndex} style={{ padding: '8px 0', borderBottom: layerIndex === layers.length - 1 ? 'none' : '1px solid #f0f0f0' }}>
                                                            <div style={{ fontSize: 13, color: '#909399' }}>
                                                                #{layerIndex + 1} {pkg.packageId} · {pkg.packageName}
                                                            </div>
                                                            {pkgInfo && (
                                                                <div style={{ display: 'flex', gap: 24, marginTop: 6 }}>
                                                                    <div style={{ minWidth: 110 }}>
                                                                        <div style={priceFieldLabel}>国内售价</div>
                                                                        <DomesticPrice pkg={pkgInfo} />
                                                                    </div>
                                                                    <div style={{ flex: 1 }}>
                                                                        <div style={priceFieldLabel}>海外售价（区域定价）</div>
                                                                        <OverseasPrice pkg={pkgInfo} />
                                                                    </div>
                                                                    <div style={{ minWidth: 90 }}>
                                                                        <div style={priceFieldLabel}>套餐底价</div>
                                                                        <CostPrice pkg={pkgInfo} />
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div style={{ marginTop: 6, paddingLeft: 12, fontSize: 13, color: code ? '#303133' : '#909399', cursor: 'pointer' }} onClick={() => setExpandedLayer(expanded ? null : key)}>
                                                                <span style={{ marginRight: 4 }}>{expanded ? '▾' : '▸'}</span>
                                                                {code ? templateName(code) : '选择模板'}
                                                            </div>
                                                            {expanded && (
                                                                <RecommendTemplatePanel
                                                                    templates={templates}
                                                                    selectedCode={code || ''}
                                                                    showManageButton
                                                                    onSelect={c => selectTemplate(item.condition, layerIndex, c)}
                                                                    onManage={() => setManageOpen(true)}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {layers.length === 0 && <span style={{ fontSize: 13, color: '#909399' }}>该漏斗暂未配置套餐</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {conditionList.length === 0 && (
                                <Alert type="info" showIcon message="当前推荐位未配置条件枚举，请在 slotConditions 中补充" />
                            )}
                        </div>

                    <Form.Item label="弹窗间隔（H）" name="popupInterval" rules={[{ required: true, message: '请输入弹窗间隔' }]} style={{ marginTop: 20 }}>
                        <InputNumber min={0} precision={0} placeholder="如：2" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="冷却期（天）" name="cooldownDays">
                        <InputNumber min={1} precision={0} placeholder="如：3" style={{ width: '100%' }} />
                    </Form.Item>
                </div>

                {/* 第三步：投放配置 */}
                <div style={{ display: step === 2 ? 'block' : 'none' }}>
                    <Form.Item label="投放区域" name="deliveryRegion">
                        <Checkbox.Group disabled={isDefaultStrategy} style={checkboxGroupStyle} options={[{ label: '国内', value: 1 }, { label: '海外', value: 2 }]} />
                    </Form.Item>
                    <Form.Item label="投放用户分群" name="userSegments">
                        <Checkbox.Group disabled={isDefaultStrategy} style={checkboxGroupStyle}>
                            {RECOMMEND_USER_SEGMENTS.map(s => (
                                <Checkbox key={s.id} value={s.id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginLeft: 8 }}>
                                        <span>{s.name}</span>
                                        <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{s.id}</span>
                                    </div>
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>
                    {isDefaultStrategy && <Alert type="info" showIcon message="默认策略的用户分群不可编辑" />}
                </div>
            </Form>

            <RecommendTemplateManageDialog
                open={manageOpen}
                templates={templates}
                onCancel={() => setManageOpen(false)}
                onChange={onTemplatesChange}
            />
        </Drawer>
    );
};

export default RecommendStrategyDrawer;
